/**
 * 全站导航单一来源：侧栏、移动端底栏、板块子页标签、面包屑都从这里读。
 *
 * 信息架构按内容流水线组织：工作台 → 选题 → 创作 → 内容 → 发布 → 数据，外加自动化与设置。
 * tab.match：除 to 以外也算「当前子页」的路径前缀（含旧路径）；tab.exact：仅 to 完全相等时激活；
 * tab.desc：显示在板块标题旁的子页说明（页面内不再自带标题）。
 */

export const SECTIONS = [
  {
    key: 'home',
    label: '工作台',
    icon: 'House',
    to: '/',
    exact: true,
    desc: '告诉 Agent 要做什么，一眼看清流水线每个环节',
  },
  {
    key: 'discover',
    label: '选题',
    icon: 'Search',
    to: '/discover/xhs',
    match: ['/discover'],
    desc: '找热点、看爆款、沉淀素材与话题',
    tabs: [
      { to: '/discover/xhs', label: '小红书发现', exact: true, desc: '按关键词或链接找笔记，看详情、评论和原文，保存到发现库' },
      { to: '/discover/douyin', label: '抖音发现', exact: true, desc: '用矩阵抖音号搜索视频，看详情与评论，保存到发现库' },
      {
        to: '/discover/library',
        label: '发现库',
        match: ['/discover/library', '/discover/xhs-library', '/discover/douyin-library'],
        desc: '各平台发现保存下来的素材，可一键洗稿',
      },
      { to: '/discover/xhs-comments', label: '评论监控', desc: '监控小红书笔记评论，关键词预警并直接回复' },
      { to: '/discover/topic-pool', label: '话题池', desc: '维护批量生成时轮换使用的话题' },
    ],
  },
  {
    key: 'create',
    label: '创作',
    icon: 'EditPen',
    to: '/create',
    match: ['/create'],
    desc: '选一种创作方式，批量产出可发布的内容',
    tabs: [
      { to: '/create', label: '全部方式', exact: true },
      { to: '/create/generate', label: '批量生成', desc: '按「主题 × 文章类型」矩阵批量生成，自动审核去重' },
      { to: '/create/zhihu-qa', label: '知乎问答', desc: '联网检索 → 拆回答方向 → 逐个方向写正文（固定知乎）' },
      { to: '/create/column-oral', label: '专栏口播', desc: '把专栏选题写成口播文案，并输出结构化引用素材' },
      { to: '/create/voice2video', label: '口播转视频', desc: '口播文案 → 配素材 → TTS 配音 → 合成视频' },
      { to: '/create/rewrite', label: '洗稿', desc: 'B站 / 抖音视频转写后改写成图文，或直接改写文本' },
      { to: '/create/tabstory', label: 'Tabstory', desc: 'Quora GEO 发帖生成器' },
    ],
  },
  {
    key: 'content',
    label: '内容',
    icon: 'Document',
    to: '/content/library',
    match: ['/content'],
    desc: '审核、编辑与排期所有生成的内容',
    tabs: [
      { to: '/content/library', label: '内容库', desc: '审核、编辑、导出与发布所有生成的文章' },
      { to: '/content/tasks', label: '生成任务', desc: '生成与洗稿任务的进度、过审结果和发布入口' },
      { to: '/content/workbench', label: '待处理草稿', desc: '运营 Agent 产出的笔记、脚本、标题与封面 A/B' },
      { to: '/content/calendar', label: '内容日历', desc: '排期、周计划、主动建议与发布后归因' },
    ],
  },
  {
    key: 'publish',
    label: '发布',
    icon: 'Promotion',
    to: '/publish',
    match: ['/publish'],
    badgeKey: 'cookie',
    desc: '矩阵账号、发布队列与登录短信；作品数据在「数据」',
  },
  {
    key: 'data',
    label: '数据',
    icon: 'DataAnalysis',
    to: '/data-analytics',
    match: ['/data-analytics'],
    desc: '账号、分组与作品数据',
  },
  {
    key: 'automation',
    label: '自动化',
    icon: 'MagicStick',
    to: '/automation/strategy',
    match: ['/automation'],
    desc: '设计账号策略 → 定时调研 → 选题与黄金流程',
  },
  {
    key: 'settings',
    label: '设置',
    icon: 'Setting',
    to: '/settings/system',
    match: ['/settings'],
    desc: '模型、提示词、设备与短信',
    tabs: [
      { to: '/settings/system', label: '系统设置', desc: '模型与提供商、敏感词审核、系统状态' },
      { to: '/settings/prompts', label: '提示词管理', desc: '各创作方式使用的提示词模板' },
      { to: '/settings/prompt-lab', label: '提示词实验室', desc: '对话式生成提示词，用真实样本测试并迭代优化' },
      { to: '/settings/devices', label: '设备与号码', desc: '手机号资费监控与短信转发设备；验证码会在顶栏实时提示' },
      { to: '/settings/sms', label: '短信网关', desc: '短信记录、转发设备、规则与钉钉机器人' },
    ],
  },
]

/** 侧栏分组 */
export const NAV_GROUPS = [
  { label: '', keys: ['home'] },
  { label: '内容流水线', keys: ['discover', 'create', 'content', 'publish', 'data'] },
  { label: '运营', keys: ['automation'] },
  { label: '系统', keys: ['settings'], lower: true },
]

/** 移动端底栏 */
export const BOTTOM_NAV_KEYS = ['home', 'create', 'content', 'publish']

const BY_KEY = Object.fromEntries(SECTIONS.map((s) => [s.key, s]))

export function sectionByKey(key) {
  return BY_KEY[key] || null
}

function pathMatches(path, prefix) {
  return path === prefix || path.startsWith(`${prefix}/`)
}

export function isItemActive(item, path) {
  if (item.exact) return path === item.to
  const match = item.match || [item.to]
  return match.some((m) => pathMatches(path, m))
}

/** 当前路径属于哪个板块 */
export function sectionForPath(path) {
  for (const s of SECTIONS) {
    if (s.key !== 'home' && isItemActive(s, path)) return s
  }
  return path === '/' ? BY_KEY.home : null
}

/** 当前路径命中的子页标签 */
export function activeTabForPath(section, path) {
  if (!section?.tabs) return null
  return section.tabs.find((t) => isItemActive(t, path)) || null
}
